import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { useDeshacer } from '@/hooks/deshacer-contexto';
import { Superfiltro } from '@/components/datos/Superfiltro';
import { useFiltrosEnUrl } from '@/hooks/useFiltrosEnUrl';
import { opcionesDe, sinFiltros, textoDeResultados, type Faceta } from '@/lib/superfiltro';
import * as asuntosApi from '@/api/asuntos.api';
import type { Asunto, Cajon } from '@/api/asuntos.api';
import { HojaDelAsunto } from '@/components/athena/HojaDelAsunto';
import { NuevoAsunto } from '@/components/athena/NuevoAsunto';
import { TarjetaDeAsunto } from '@/components/athena/TarjetaDeAsunto';

/**
 * ME TOCA — la bandeja única.
 *
 * Mandato: *"Donde fusionamos lo que hoy son Mi día + Inspector. Esas dos
 * pantallas desaparecen."*
 *
 * Y desaparecen de verdad, no se esconden del menú: la misma tarea vivía en
 * dos pantallas con dos diseños, y dos vistas del mismo dato terminan siempre
 * contradiciéndose. Todo sale de `/api/asuntos`, igual que Hoy y Agenda.
 *
 * ═══ "RUIDO" SE PUEDE MIRAR ═══
 *
 * Lo que Athena descartó sola sigue estando, en su propio chip, y se rescata
 * con un toque. Esconderlo del todo sería pedirle al dueño que confíe en un
 * filtro que no puede auditar, cuando lo que se le filtra es plata.
 */

type Chip = {
  id: string;
  etiqueta: string;
  filtro: (a: Asunto) => boolean;
  /** Los que piden datos que la lista normal no trae. */
  pide?: Partial<asuntosApi.FiltroDeAsuntos>;
};

const CHIPS: Chip[] = [
  {
    id: 'por-aprobar',
    etiqueta: 'Por aprobar',
    filtro: (a) => a.estado === 'DETECTADO',
  },
  {
    id: 'yo-prometi',
    etiqueta: 'Yo prometí',
    filtro: (a) => !a.responsable && ['ENVIAR_INFO', 'COTIZACION', 'ESCRIBIR'].includes(a.tipo),
  },
  {
    id: 'me-prometieron',
    etiqueta: 'Me prometieron',
    filtro: (a) => a.cajon === 'DINERO' || a.tipo === 'SEGUIMIENTO',
  },
  {
    id: 'delegado',
    etiqueta: 'Delegado',
    filtro: (a) => Boolean(a.responsable),
  },
  {
    id: 'ruido',
    etiqueta: 'Ruido',
    filtro: (a) => a.prioridad === 'RUIDO' || a.estado === 'DESCARTADO',
    pide: { incluirRuido: true, estado: 'todos' },
  },
];

const CAJONES: Array<{ valor: Cajon; etiqueta: string }> = [
  { valor: 'TRABAJO', etiqueta: 'Trabajo' },
  { valor: 'DINERO', etiqueta: 'Dinero' },
  { valor: 'CLIENTES', etiqueta: 'Clientes' },
  { valor: 'PERSONAL', etiqueta: 'Personal' },
];

export default function MeToca() {
  // SUPERFILTRO (Josué, 14-oct): los chips de antes son la faceta «Qué»; se suman cajón y responsable.
  const { filtros, ponerVarios } = useFiltrosEnUrl(
    { chip: '', cajon: '', responsable: '', buscar: '' },
    { chip: CHIPS.map((c) => c.id), cajon: CAJONES.map((c) => c.valor) },
  );
  const [abierto, setAbierto] = useState<string | null>(null);
  const [anotando, setAnotando] = useState(false);
  const navegar = useNavigate();
  const toast = useToast();
  const { ofrecerDeshacer } = useDeshacer();
  const qc = useQueryClient();

  const chipActivo = filtros.chip || 'todos';
  const cajon = (filtros.cajon || undefined) as Cajon | undefined;
  const chip = CHIPS.find((c) => c.id === chipActivo);

  const asuntos = useQuery({
    queryKey: ['asuntos', 'me-toca', chipActivo, cajon],
    queryFn: () => asuntosApi.getAsuntos({ limite: 300, cajon, ...(chip?.pide ?? {}) }),
  });

  const posponer = useMutation({
    mutationFn: ({ id, hasta }: { id: string; hasta: string }) =>
      asuntosApi.moverAsunto(id, 'posponer', { hasta }),
    onSuccess: () => {
      toast.success('Pospuesto');
      void qc.invalidateQueries({ queryKey: ['asuntos'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const mover = useMutation({
    mutationFn: ({ id, accion }: { id: string; accion: 'aprobar' | 'descartar' | 'rescatar' }) =>
      asuntosApi.moverAsunto(id, accion),
    onSuccess: (_d, v) => {
      const dicho = { aprobar: 'Aprobado', descartar: 'Descartado', rescatar: 'Rescatado' };
      void qc.invalidateQueries({ queryKey: ['asuntos'] });
      // Deshacer en vez de un aviso: con la red puesta, deslizar sin mirar es
      // barato, y deslizar sin mirar es lo que se hace con la lista del dia.
      //
      // "Volver a DETECTADO" es lo correcto y no "al estado anterior": el
      // anterior habria que guardarlo, y un deshacer que a veces restaura mal
      // es peor que no tenerlo.
      ofrecerDeshacer({
        texto: dicho[v.accion],
        deshacer: async () => {
          await asuntosApi.moverAsunto(v.id, 'rescatar');
          void qc.invalidateQueries({ queryKey: ['asuntos'] });
          toast.success('Deshecho');
        },
      });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const todos: Asunto[] = asuntos.data?.asuntos ?? [];
  const q = filtros.buscar.trim().toLowerCase();
  const lista = (chip ? todos.filter(chip.filtro) : todos).filter(
    (a) =>
      (!filtros.responsable || a.responsable?.nombre === filtros.responsable) &&
      (!q || `${a.descripcion} ${a.conQuien} ${a.codigo}`.toLowerCase().includes(q)),
  );
  const esRuido = chipActivo === 'ruido';

  const facetas: Faceta[] = [
    { clave: 'chip', titulo: 'Qué', opciones: CHIPS.map((c) => ({ valor: c.id, etiqueta: c.etiqueta })) },
    { clave: 'cajon', titulo: 'Cajón', opciones: CAJONES },
    { clave: 'responsable', titulo: 'Responsable', opciones: opcionesDe(todos, (a) => a.responsable?.nombre) },
  ];

  return (
    <div className="space-y-3">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">Me toca</h1>
        <button
          onClick={() => setAnotando(true)}
          className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-acento-lleno text-acento-sobre"
          aria-label="Anotar algo"
        >
          <Plus className="h-5 w-5" />
        </button>
        <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">
          Todo lo que Athena sacó de tus conversaciones.
        </p>
      </div>

      <Superfiltro
        buscar={filtros.buscar}
        onBuscar={(v) => ponerVarios({ buscar: v })}
        placeholder="Buscar por asunto o persona…"
        facetas={facetas}
        valores={filtros}
        onCambiar={ponerVarios}
        onLimpiar={() => ponerVarios(sinFiltros(facetas))}
        resultados={asuntos.isPending ? undefined : textoDeResultados(lista.length, todos.length, ['asunto', 'asuntos'])}
      />

      {esRuido && (
        <p className="rounded-lg bg-slate-100 p-2.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          Esto es lo que Athena descartó sola. Si se equivocó, rescátalo: nada se borra.
        </p>
      )}

      {/* En escritorio la lista va a dos columnas: a 1440 px una sola deja
          media pantalla vacia y obliga a bajar el doble. La hoja del asunto
          sigue abriendo como panel derecho encima. */}
      <Card>
        <CardBody className="space-y-0 xl:columns-2 xl:gap-6 [&>*]:break-inside-avoid">
          {asuntos.isPending ? (
            <Spinner />
          ) : asuntos.isError ? (
            <p className="py-6 text-center text-sm text-red-600 dark:text-red-300">
              No se pudieron leer tus asuntos.
            </p>
          ) : lista.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-400">
              {esRuido
                ? 'Athena no ha descartado nada todavía.'
                : chip
                  ? `Nada en "${chip.etiqueta}".`
                  : 'No tienes nada abierto. Cuando Athena lea tus conversaciones, aparece aquí.'}
            </p>
          ) : (
            lista.map((a) => (
              <TarjetaDeAsunto
                key={a.id}
                asunto={a}
                conFecha
                onAbrir={setAbierto}
                onAprobar={
                  esRuido
                    ? (id) => mover.mutate({ id, accion: 'rescatar' })
                    : (id) => mover.mutate({ id, accion: 'aprobar' })
                }
                onDescartar={
                  esRuido ? undefined : (id) => mover.mutate({ id, accion: 'descartar' })
                }
                onPosponer={(id, hasta) => posponer.mutate({ id, hasta })}
                onVerMensaje={(x) =>
                  navegar(
                    `/chats/${x.conversationId}${x.mensajeId ? `?mensaje=${x.mensajeId}` : ''}`,
                  )
                }
              />
            ))
          )}
        </CardBody>
      </Card>

      <NuevoAsunto abierto={anotando} onCerrar={() => setAnotando(false)} />
      <HojaDelAsunto asuntoId={abierto} onCerrar={() => setAbierto(null)} />
    </div>
  );
}
